import Link from 'next/link'

import classNames from 'classnames'

import Button, {
  ButtonTypes,
  ButtonVariants,
} from '~/components/atoms/Button/Button'
import { Icon } from '~/components/atoms/Icon/Icon'
import type { ImageProps } from '~/components/atoms/Image/Image'
import Lozenge, { LozengeVariant } from '~/components/atoms/Lozenge/Lozenge'
import SpecificImage from '~/components/atoms/SpecificImage/SpecificImage'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { toURL } from '~/utils/urlResolver'

import Badge from '../Badge/Badge'

import styles from './PartnerCard.module.css'

export type PartnerCategories = {
  name: string
  slug: string
  summary?: string
  image?: ImageProps
  categoryOrder?: number
}

export interface PartnerProps {
  name: string
  image?: ImageProps
  type?: 'accredited' | 'sponsored' | 'trusted'
  summary?: string
  category?: PartnerCategories[]
  town?: string
  city?: string
  portrait?: boolean
  theme?: 'dark' | 'light'
  telephone?: string
  url: {
    _type: string
    slug: string
  }
  geoLocation: {
    lat: number
    lng: number
  }
  isFeatured?: boolean
  siteUrl?: string
  slug?: {
    current: string
    _type: string
  }
}

export interface PartnerCardProps extends PartnerProps {
  portrait?: boolean
  theme?: 'dark' | 'light'
  noLazyLoad?: boolean
}

export default function PartnerCard({
  name,
  image,
  summary,
  category,
  town,
  city,
  portrait = true,
  theme = 'dark',
  noLazyLoad = true,
  url,
  type,
}: PartnerCardProps) {
  const isSponsored = type === 'sponsored'
  const isAccredited = type === 'accredited'
  const isTrusted = type === 'trusted'
  return (
    <>
      <div
        className={classNames(
          styles.base,
          !portrait && styles.baseLandscape,
          styles[`${theme}BaseTheme`]
        )}
      >
        <Link href={toURL(url)}>
          <div className={styles.container}>
            <div className={styles.imageContainer}>
              {isAccredited && (
                <div className={styles.accredditedPartnerBadge}>
                  <Icon type="AccreditedPartner" />
                </div>
              )}
              <div className={styles.imageContainerInner}>
                {image && image.asset && (
                  <SpecificImage
                    asset={image.asset}
                    alt={`${name} - MyArtBroker`}
                    width={400}
                    noLazyLoad={noLazyLoad}
                  />
                )}
              </div>
            </div>
            <div className={styles.details}>
              <div className={styles.detailsInner}>
                <div className={styles.titleContainer}>
                  <Text
                    size={TextSizes.primaryTitleMed}
                    className={styles.title}
                  >
                    {name}
                  </Text>
                  {type && (
                    <div className={styles.classifications}>
                      {isSponsored && (
                        <Lozenge
                          variant={LozengeVariant.ghostDark}
                          size="small"
                          title="sponsored"
                          count=""
                        />
                      )}
                      {isTrusted && <Badge type={'trusted'} />}
                    </div>
                  )}
                </div>
                <div className={styles.location}>
                  <div className={styles.locationIcon}>
                    <Icon
                      type={
                        theme === 'light' ? 'GeoLocationLight' : 'GeoLocation'
                      }
                    />
                  </div>
                  <div className={styles.locationName}>
                    {town && (
                      <Text size={TextSizes.bodyMed} className={styles.town}>
                        {town}
                      </Text>
                    )}
                    {town && city && (
                      <Text size={TextSizes.bodyMed}>{', '}</Text>
                    )}

                    <Text size={TextSizes.bodyMed} className={styles.city}>
                      {city}
                    </Text>
                  </div>
                </div>
                {category && category.length > 0 && (
                  <div className={styles.categories}>
                    {category.map(
                      cat =>
                        cat.name && (
                          <div key={cat.name} className={styles.category}>
                            <Text
                              size={TextSizes.bodyMed}
                              uppercase
                              className={styles.categoryName}
                            >
                              {cat.name}
                            </Text>
                          </div>
                        )
                    )}
                  </div>
                )}
                <Text size={TextSizes.bodyMed} className={styles.summary}>
                  {summary}
                </Text>

                <Button
                  variant={ButtonVariants.capsUnderlineSml}
                  button={{
                    type: ButtonTypes.button,
                  }}
                  className={styles.ctaLink}
                >
                  View details
                </Button>
              </div>
            </div>
          </div>
        </Link>
      </div>
    </>
  )
}
